/**
 * How fast the pages an assistant sent people to actually are.
 *
 * Here rather than as a performance product: a crawler works to a budget, and
 * a page it gives up on is a page that never gets read, let alone cited. So
 * this sits at the end of the loop next to the traffic it explains, and the
 * figures are the ones Google publishes thresholds for.
 *
 * The 75th percentile, not the mean: a mean hides the slow quarter, and the
 * slow quarter is the part that loses the fetch.
 */

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { api, type TrafficVitalsRow } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { SourceNote, TrafficEmpty, Unavailable } from "~/modules/analytics/pages/tracking/shared";

/**
 * Google's "needs improvement" thresholds, in the units each metric reports.
 *
 * Only the upper bound is kept: this screen says which pages are a problem,
 * and a three-colour scale would put two-thirds of a table in colour on a
 * workbench that allows one accent.
 */
const POOR: Record<string, number> = { lcp: 4000, inp: 500, cls: 0.25, fcp: 3000, ttfb: 1800 };

export function VitalsPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "traffic", "vitals", filters.range],
		queryFn: () => api.trafficVitals(entityId, { range: filters.range }),
		...read,
	});

	return (
		<StageShell entityId={entityId} reading="vitals" lede={t("vitals.lede")} filters={filters} setFilters={setFilters}>
			<Async state={state}>
				{(data) => (
					<>
						<SourceNote frame={data} />
						{!data.available || data.setup_required ? (
							<Unavailable frame={data} entityId={entityId} />
						) : (
							<Section title={t("vitals.pages")} lede={t("vitals.pagesHint")}>
								{data.pages.length === 0 ? (
									<TrafficEmpty title="vitals.emptyTitle" body="vitals.empty" entityId={entityId} />
								) : (
									<Rows rows={data.pages} />
								)}
							</Section>
						)}
					</>
				)}
			</Async>
		</StageShell>
	);
}

function Rows({ rows }: { rows: TrafficVitalsRow[] }) {
	const t = useT();
	const { locale } = useI18n();
	return (
		<div className="table-scroll">
			<Table variant="resource">
				<TableHeader>
					<TableRow>
						<TableHead className="wrap">{t("vitals.path")}</TableHead>
						<TableHead className="num">{t("vitals.views")}</TableHead>
						<TableHead className="num">LCP</TableHead>
						<TableHead className="num">INP</TableHead>
						<TableHead className="num">CLS</TableHead>
						<TableHead className="num">FCP</TableHead>
						<TableHead className="num">TTFB</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.path}>
							<TableCell className="wrap mono">{row.path}</TableCell>
							<TableCell className="num">{fmt.number(locale, row.views)}</TableCell>
							<Cell metric="lcp" value={row.lcp} unit="ms" />
							<Cell metric="inp" value={row.inp} unit="ms" />
							<Cell metric="cls" value={row.cls} unit="" />
							<Cell metric="fcp" value={row.fcp} unit="ms" />
							<Cell metric="ttfb" value={row.ttfb} unit="ms" />
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

function Cell({ metric, value, unit }: { metric: string; value: number | null; unit: string }) {
	const { locale } = useI18n();
	if (value === null) return <TableCell className="num">—</TableCell>;
	const poor = value > POOR[metric];
	return (
		// The only colour on this screen, and it is a state rather than an
		// emphasis: past the threshold the page is losing fetches.
		<TableCell className={poor ? "num warn" : "num"}>
			{unit === "ms" ? `${fmt.number(locale, Math.round(value))} ms` : value.toFixed(3)}
		</TableCell>
	);
}
